/**
 * Minimal canvas confetti burst, purpose-built for the spin-to-win reveal in
 * assets/email-popup.js -- not a general-purpose confetti library. Kept
 * deliberately small (one canvas, ~90 particles, plain fillRect draws) with
 * a hard lifetime cap so the cost is bounded and self-cleaning: the
 * requestAnimationFrame loop stops and the canvas is removed as soon as
 * every particle has fallen off-screen, or MAX_DURATION_MS elapses,
 * whichever comes first.
 */
(() => {
  const COLORS = ['#E10202', '#ffffff', '#111111', '#f4c542'];
  const PARTICLE_COUNT = 90;
  const MAX_DURATION_MS = 3200;
  const GRAVITY = 0.12;

  /** @param {{count?: number}} [options] */
  function burst(options = {}) {
    const count = options.count ?? PARTICLE_COUNT;
    const width = window.innerWidth;
    const height = window.innerHeight;

    const canvas = document.createElement('canvas');
    canvas.style.cssText =
      'position:fixed;inset:0;width:100vw;height:100vh;pointer-events:none;z-index:2147483000;';
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    document.body.appendChild(canvas);

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      canvas.remove();
      return;
    }
    ctx.scale(dpr, dpr);

    const particles = Array.from({ length: count }, () => ({
      x: Math.random() * width,
      y: -20 - Math.random() * 200,
      vx: (Math.random() - 0.5) * 2.4,
      vy: 2 + Math.random() * 2.5,
      size: 6 + Math.random() * 6,
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
      rotation: Math.random() * 360,
      spin: (Math.random() - 0.5) * 12,
    }));

    const startTime = performance.now();
    let frameId;

    function frame(now) {
      const elapsed = now - startTime;
      ctx.clearRect(0, 0, width, height);

      let alive = false;
      for (const p of particles) {
        p.x += p.vx;
        p.y += p.vy;
        p.vy += GRAVITY;
        p.rotation += p.spin;
        if (p.y < height + 30) alive = true;

        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rotation * Math.PI) / 180);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
        ctx.restore();
      }

      if (alive && elapsed < MAX_DURATION_MS) {
        frameId = requestAnimationFrame(frame);
      } else {
        cancelAnimationFrame(frameId);
        canvas.remove();
      }
    }

    frameId = requestAnimationFrame(frame);
  }

  window.miniConfetti = { burst };
})();
